import type { EdgeDashStyle, EdgeType } from '../types';
import {
  CORE_DRAWING_SHAPE_TYPES,
  GENERAL_SHAPE_TYPES,
  KIT_SHAPE_TYPES,
  MARKUP_TYPES,
  NETWORK_NODE_TYPES,
  NODE_TYPES,
  PIPELINE_NODE_TYPES,
  UML_NODE_TYPES,
} from '../constants/nodeTypes';

/**
 * A kit is a palette + default policies on the shared flow canvas.
 * Sequence stays its own canvas (time / lifelines). Network, pipeline, and
 * UML are kits — not forked editors.
 */
export type DiagramKitId = 'architecture' | 'sketch' | 'network' | 'pipeline' | 'uml';

export type KitLayoutHint = 'free' | 'ltr' | 'ttb';

export interface DiagramKit {
  id: DiagramKitId;
  label: string;
  shortLabel: string;
  description: string;
  /** Palette groups shown in the left sidebar. */
  palette: { title: string; types: string[] }[];
  drawTools: Array<'box' | 'text' | 'note' | 'arrow' | 'line'>;
  defaultEdge: { edgeType: EdgeType; dashStyle?: EdgeDashStyle };
  layout: KitLayoutHint;
  magnetic: boolean;
  usesCatalog: boolean;
}

export const ARCHITECTURE_NODE_TYPES: string[] = Object.keys(NODE_TYPES).filter(
  type =>
    type !== 'box'
    && !GENERAL_SHAPE_TYPES.has(type)
    && !MARKUP_TYPES.has(type)
    && !KIT_SHAPE_TYPES.has(type),
);

export const SKETCH_CORE_TYPES: string[] = ['box'];

export const DIAGRAM_KITS: Record<DiagramKitId, DiagramKit> = {
  architecture: {
    id: 'architecture',
    label: 'Architecture',
    shortLabel: 'Arch',
    description: 'Services, data stores, and C4-style system maps. Typed sockets.',
    palette: [{ title: 'Components', types: ARCHITECTURE_NODE_TYPES }],
    drawTools: ['box', 'text', 'note', 'arrow', 'line'],
    defaultEdge: { edgeType: 'arrow' },
    layout: 'ltr',
    magnetic: false,
    usesCatalog: true,
  },
  sketch: {
    id: 'sketch',
    label: 'Sketch',
    shortLabel: 'Sketch',
    description: 'Boxes and shapes. Click a tool once to place once; click again to keep drawing.',
    palette: [
      { title: 'Shapes', types: [...SKETCH_CORE_TYPES, ...CORE_DRAWING_SHAPE_TYPES] },
    ],
    drawTools: ['box', 'text', 'note', 'arrow', 'line'],
    defaultEdge: { edgeType: 'arrow' },
    layout: 'free',
    magnetic: true,
    usesCatalog: false,
  },
  network: {
    id: 'network',
    label: 'Network',
    shortLabel: 'Net',
    description: 'Clouds, hosts, firewalls, and links. Drop on a node — no socket hunting.',
    palette: [{ title: 'Network', types: [...NETWORK_NODE_TYPES] }],
    drawTools: ['text', 'note', 'arrow', 'line'],
    defaultEdge: { edgeType: 'no-arrow' },
    layout: 'free',
    magnetic: true,
    usesCatalog: false,
  },
  pipeline: {
    id: 'pipeline',
    label: 'Pipeline',
    shortLabel: 'Pipe',
    description: 'Sources, transforms, stores, sinks. Beautify runs left to right.',
    palette: [{ title: 'Pipeline', types: [...PIPELINE_NODE_TYPES] }],
    drawTools: ['text', 'note', 'arrow', 'line'],
    defaultEdge: { edgeType: 'arrow' },
    layout: 'ltr',
    magnetic: true,
    usesCatalog: false,
  },
  uml: {
    id: 'uml',
    label: 'UML',
    shortLabel: 'UML',
    description: 'Classes, interfaces, actors. Inheritance and composition on edges.',
    palette: [{ title: 'UML', types: [...UML_NODE_TYPES] }],
    drawTools: ['text', 'note', 'arrow', 'line'],
    defaultEdge: { edgeType: 'arrow' },
    layout: 'ttb',
    magnetic: true,
    usesCatalog: false,
  },
};

export const DIAGRAM_KIT_IDS = Object.keys(DIAGRAM_KITS) as DiagramKitId[];

export function isDiagramKitId(value: unknown): value is DiagramKitId {
  return typeof value === 'string' && value in DIAGRAM_KITS;
}

export function getKit(id?: string | null): DiagramKit {
  if (id && isDiagramKitId(id)) return DIAGRAM_KITS[id];
  return DIAGRAM_KITS.architecture;
}

export function kitQuickCreateTypes(kitId?: string | null): string[] {
  const kit = getKit(kitId);
  const types = kit.palette.flatMap(group => group.types);
  return types.filter(type => type !== 'waypoint' && type !== 'group');
}

/**
 * Which way Beautify should rank the diagram.
 *
 * The kit knows what a diagram of its sort usually wants - a sequence reads
 * downward, an architecture map reads across - and that is a good default but
 * a bad rule, because it is a guess about a drawing the author can see and the
 * kit cannot. `'auto'` keeps the guess; anything else is the author saying they
 * have looked at it and want it the other way.
 */
export type LayoutDirection = 'auto' | 'ttb' | 'ltr';

export function resolveRankAxis(
    preference: LayoutDirection | undefined,
    kitLayout: KitLayoutHint,
): 'ttb' | 'ltr' | undefined {
    if (preference && preference !== 'auto') return preference;
    // 'free' is the kit declining to have an opinion, and so is undefined here:
    // it leaves the engine on its own default rather than inventing one.
    return kitLayout === 'ttb' || kitLayout === 'ltr' ? kitLayout : undefined;
}
